/**
 * Shared utility functions for AI Summary formatting
 */

const REMOVE_KEYS_LIST = ["Conversation Duration", "ConversationDuration"];

/**
 * Parse the AI summary message content
 * @param {object} summary - The raw summary object from API
 * @returns {object} Parsed content object
 */
export const parseSummaryContent = (summary) => {
    const messageContent = summary?.choices?.[0]?.message?.content;
    if (!messageContent) return {};

    try {
        return typeof messageContent === "string"
            ? JSON.parse(messageContent)
            : messageContent;
    } catch (error) {
        console.error("Failed to parse messageContent:", error);
        return {};
    }
};

/**
 * Format keys nicely (CUSTOMER_SATISFACTION → Customer Satisfaction)
 * @param {string} key - The key to format
 * @returns {string} Formatted title
 */
export const formatTitle = (key) => {
    if (typeof key !== "string") return key;
    return key
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

/**
 * Convert object values into a readable string
 * @param {object} obj - Object to format
 * @returns {string} Formatted string
 */
export const formatObjectValue = (obj) =>
    Object.entries(obj)
        .map(([k, v]) => `${formatTitle(k)}: ${v}`)
        .join(", ");

/**
 * Check if a value is empty
 * @param {any} value - Value to check
 * @returns {boolean} True if empty
 */
export const isEmptyValue = (value) => {
    return (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0)
    );
};

/**
 * Check if a key should be removed from display
 * @param {string} key - Key to check
 * @returns {boolean} True if should be removed
 */
export const shouldRemoveKey = (key) => REMOVE_KEYS_LIST.includes(key);

/**
 * Format value as plain text (for download)
 * @param {any} value - Value to format
 * @returns {string} Formatted plain text
 */
export const formatValueAsText = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) =>
            typeof item === "object" ? `  - ${formatObjectValue(item)}` : `  - ${item}`
        ).join("\n");
    }
    if (typeof value === "object" && value !== null) {
        return formatObjectValue(value);
    }
    return value;
};

/**
 * Generate plain text summary for download
 * @param {object} parsedContent - Parsed summary content
 * @returns {string} Formatted plain text summary
 */
export const generateSummaryText = (parsedContent) => {
    let summaryText = "";

    Object.entries(parsedContent).forEach(([key, value]) => {
        if (!shouldRemoveKey(key) && !isEmptyValue(value)) {
            summaryText += `${formatTitle(key)}:\n${formatValueAsText(value)}\n\n`;
        }
    });

    return summaryText;
};
